/*
 * Announcement bar rotator (dependency-free).
 *
 * Drives #AnnouncementSlider / .announcement-slider--js without Flickity so the
 * top bar reliably cycles through its slides on every breakpoint.
 *
 * Behaviour:
 *   - Single slide        -> static, no animation, no autoplay.
 *   - prefers-reduced-motion -> no autoplay; first slide shown, others reachable
 *                               only via the theme editor / focus.
 *   - Pauses on hover and while a link inside the bar has keyboard focus.
 *   - Accessibility: non-visible slides are aria-hidden so screen readers read
 *     only the current message; container stays aria-live="off" (rotating
 *     marketing copy should not spam assistive tech). Links stay focusable.
 *   - Theme editor: re-inits on shopify:section:load and honours block select.
 */
(function () {
  'use strict';

  var SELECTOR = '.announcement-slider--js';
  var ACTIVE = 'is-selected';
  var instances = [];

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function Rotator(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(
      root.querySelectorAll('.announcement-slider__slide')
    );
    this.index = 0;
    this.timer = null;
    this.paused = false;

    var speed = parseFloat(root.getAttribute('data-speed'));
    this.delay = (isNaN(speed) ? 5 : speed) * 1000;
    if (this.delay < 2000) {
      this.delay = 2000;
    }

    this.autoplay =
      root.getAttribute('data-autoplay') !== 'false' &&
      this.slides.length > 1 &&
      !prefersReducedMotion();

    this.init();
  }

  Rotator.prototype.init = function () {
    if (!this.slides.length) {
      return;
    }

    // Normalise: show the first slide, hide the rest.
    this.show(0, true);

    // Mark as enabled so CSS can stack/fade the slides.
    this.root.classList.add('announcement-slider--ready');

    if (this.slides.length < 2) {
      return; // single slide: nothing to rotate
    }

    var self = this;

    this.onEnter = function () {
      self.pause();
    };
    this.onLeave = function () {
      self.resume();
    };
    this.onFocusIn = function () {
      self.pause();
    };
    this.onFocusOut = function () {
      self.resume();
    };

    this.root.addEventListener('mouseenter', this.onEnter);
    this.root.addEventListener('mouseleave', this.onLeave);
    this.root.addEventListener('focusin', this.onFocusIn);
    this.root.addEventListener('focusout', this.onFocusOut);

    // Pause when the tab is not visible (saves work, avoids jumpy catch-up).
    this.onVisibility = function () {
      if (document.hidden) {
        self.stop();
      } else {
        self.resume();
      }
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    if (this.autoplay) {
      this.start();
    }
  };

  Rotator.prototype.show = function (i, immediate) {
    for (var s = 0; s < this.slides.length; s++) {
      var slide = this.slides[s];
      var current = s === i;
      slide.classList.toggle(ACTIVE, current);
      // Keep only the visible slide exposed to assistive tech.
      if (current) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }
    }
    this.index = i;
    if (immediate) {
      // no-op: CSS handles the transition; immediate just sets state
    }
  };

  Rotator.prototype.next = function () {
    var i = (this.index + 1) % this.slides.length;
    this.show(i);
  };

  Rotator.prototype.start = function () {
    if (this.timer || this.slides.length < 2 || !this.autoplay) {
      return;
    }
    var self = this;
    this.timer = window.setInterval(function () {
      if (!self.paused) {
        self.next();
      }
    }, this.delay);
  };

  Rotator.prototype.stop = function () {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  };

  Rotator.prototype.pause = function () {
    this.paused = true;
  };

  Rotator.prototype.resume = function () {
    if (document.hidden) {
      return;
    }
    this.paused = false;
    this.start();
  };

  // Theme editor hook: jump to the selected block and hold.
  Rotator.prototype.goToBlock = function (id) {
    for (var s = 0; s < this.slides.length; s++) {
      if (this.slides[s].id === 'AnnouncementSlide-' + id) {
        this.show(s);
        this.pause();
        return;
      }
    }
  };

  Rotator.prototype.destroy = function () {
    this.stop();
    if (this.onEnter) {
      this.root.removeEventListener('mouseenter', this.onEnter);
      this.root.removeEventListener('mouseleave', this.onLeave);
      this.root.removeEventListener('focusin', this.onFocusIn);
      this.root.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
  };

  function initAll(scope) {
    var root = scope || document;
    var bars = root.querySelectorAll(SELECTOR);
    for (var i = 0; i < bars.length; i++) {
      var el = bars[i];
      if (el.__aabRotator) {
        continue; // already initialised
      }
      el.__aabRotator = new Rotator(el);
      instances.push(el.__aabRotator);
    }
  }

  function destroyIn(scope) {
    for (var i = instances.length - 1; i >= 0; i--) {
      var inst = instances[i];
      if (!scope || scope.contains(inst.root)) {
        inst.destroy();
        if (inst.root) {
          inst.root.__aabRotator = null;
        }
        instances.splice(i, 1);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Shopify theme editor lifecycle.
  document.addEventListener('shopify:section:load', function (evt) {
    destroyIn(evt.target);
    initAll(evt.target);
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    destroyIn(evt.target);
  });

  document.addEventListener('shopify:block:select', function (evt) {
    var root = evt.target && evt.target.closest(SELECTOR);
    if (root && root.__aabRotator) {
      root.__aabRotator.goToBlock(evt.detail.blockId);
    } else {
      var bar = document.querySelector(SELECTOR);
      if (bar && bar.__aabRotator) {
        bar.__aabRotator.goToBlock(evt.detail.blockId);
      }
    }
  });

  document.addEventListener('shopify:block:deselect', function () {
    for (var i = 0; i < instances.length; i++) {
      instances[i].resume();
    }
  });
})();
